/**
 * Aplicação de Exames Ocupacionais (#606): a configuração de exames da tela única comum
 * (`InventarioDoDestino`). Adicionar é direto, sem janela: cada exame entra obrigatório e com a
 * periodicidade do próprio cadastro, que a linha deixa mudar logo depois.
 */
import { Chip, TextField, Typography } from '@mui/material';

import { aplicarComResultadoPorItem } from '../../components/applications/resultadoPorItem';
import { contar, linhaDeVoltaASeguir } from '../../components/applications/textosDoNivel';
import type {
  AcaoEmLote,
  ConfigDeAplicacao,
  ContextoDaRemocao,
  DestinoRef,
  ValorDeCampo,
} from '../../components/applications/tipos';
import apiClient from '../../services/apiClient';
import { examApplicationService } from '../../services/examApplicationService';
import { occupationalExamService } from '../../services/occupationalExamService';
import type { ExamWithApplication } from '../../types/examApplication';
import { OccupationalExamStatus, type OccupationalExam } from '../../types/occupationalExam';
import { parseFrequencyToMonths } from '../../utils/examFrequency';
import { AvisoDaConvocacao, CampoDePeriodicidade, DetalheDoExame, SeletorDeObrigatorio } from './camposDoExame';
import {
  OBRIGATORIO,
  OPCIONAL,
  SEM_PERIODICIDADE,
  nomeDaAplicacao,
  periodicidadeDoValor,
  textoDaPeriodicidade,
  type ValoresDoExame,
} from './textosDoExame';
import { ConferirAoMexer } from '../../components/applications/ConferirAoMexer';

export type { ValoresDoExame } from './textosDoExame';

/** Na linha recém-adicionada: obrigatório e periodicidade vieram do padrão, e ninguém deve aceitá-los sem olhar. */
function Confira() {
  return <Chip size="small" color="warning" variant="outlined" label="confira" sx={{ mt: 0.5 }} />;
}

/** O que só a cópia leva: a aplicação nova nasce sem observações. */
interface CamposDaCopia {
  observations: string | null;
}

async function aplicarExames(destino: DestinoRef, ids: number[], valores: ValoresDoExame, copia?: CamposDaCopia) {
  return aplicarComResultadoPorItem(ids, 'examId', () =>
    apiClient.post(`/exam-applications/${destino.nivel}/${destino.id}/apply`, {
      examIds: ids,
      isRequired: valores.isRequired,
      periodicityMonths: valores.periodicityMonths,
      ...copia,
    })
  );
}

const ACOES_EM_LOTE: AcaoEmLote<ExamWithApplication>[] = [
  {
    id: 'mudar-obrigatorio',
    rotulo: 'Mudar obrigatório',
    campo: 'obrigatorio',
    valorInicial: null,
    escolher: (valor, mudar) => (
      <SeletorDeObrigatorio
        valor={typeof valor === 'boolean' ? valor : null}
        rotulo="Obrigatório para os marcados"
        aoMudar={mudar}
      />
    ),
  },
  {
    id: 'mudar-periodicidade',
    rotulo: 'Mudar periodicidade',
    campo: 'periodicidade',
    valorInicial: null,
    escolher: (valor, mudar) => (
      <TextField
        select
        size="small"
        value={valor === null ? '' : String(valor)}
        onChange={(evento) =>
          mudar(evento.target.value === SEM_PERIODICIDADE ? SEM_PERIODICIDADE : Number(evento.target.value))
        }
        SelectProps={{ native: true }}
        inputProps={{ 'aria-label': 'Periodicidade para os marcados' }}
        sx={{ minWidth: 180 }}
      >
        <option value="" disabled>
          Escolha a periodicidade
        </option>
        {[3, 6, 12, 24, 36].map((meses) => (
          <option key={meses} value={meses}>
            {textoDaPeriodicidade(meses)}
          </option>
        ))}
        <option value={SEM_PERIODICIDADE}>Sem periodicidade</option>
      </TextField>
    ),
    valorPara: (_aplicacao, escolhido) => periodicidadeDoValor(escolhido),
  },
];

/** Tornar opcional ou mudar a periodicidade muda o vencimento de quem o destino alcança (D5). */
function descreverEdicao(
  aplicacao: ExamWithApplication,
  campoId: string,
  depois: ValorDeCampo,
  funcionarios: number
): string {
  const nome = nomeDaAplicacao(aplicacao);
  const mudou =
    campoId === 'obrigatorio'
      ? `${nome} passou a ${depois === true ? 'obrigatório' : 'opcional'}`
      : `Periodicidade de ${nome} passou a ${textoDaPeriodicidade(periodicidadeDoValor(depois)).toLowerCase()}`;
  const afetados =
    funcionarios > 0
      ? `Muda o vencimento de ${contar(funcionarios, 'funcionário', 'funcionários')} deste destino.`
      : 'Nenhum funcionário neste destino tem o vencimento afetado.';
  return `${mudou}. ${afetados}`;
}

function textoDeRemocao(
  aplicacoes: ExamWithApplication[],
  { destino, deCima }: ContextoDaRemocao<ExamWithApplication>
): string[] {
  const umSo = aplicacoes.length === 1;
  return [
    `Apaga a periodicidade e as observações ${umSo ? 'desta aplicação' : 'destas aplicações'}.`,
    deCima.length > 0
      ? 'Exames já realizados e documentos já emitidos não mudam.'
      : `${umSo ? 'Ele sai' : 'Eles saem'} da convocação de quem está neste destino. Exames já realizados e documentos já emitidos não mudam.`,
    ...deCima.map((herdada) =>
      linhaDeVoltaASeguir(nomeDaAplicacao(herdada), destino, herdada.origem, textoDaPeriodicidade(herdada.periodicityMonths))
    ),
  ];
}

export const CONFIG_DE_EXAMES: ConfigDeAplicacao<OccupationalExam, ExamWithApplication, ValoresDoExame> = {
  familia: 'exam',
  rotulos: {
    singular: 'exame',
    plural: 'exames',
    titulo: 'Aplicação de Exames Ocupacionais',
    daTela: 'Exames',
    confira: 'Confira se é obrigatório e a periodicidade.',
  },

  AvisoDosItens: AvisoDaConvocacao,

  carregarCatalogo: async () =>
    (await occupationalExamService.getOccupationalExams({ status: OccupationalExamStatus.ACTIVE, limit: 2000 })).exams,
  item: {
    id: (exame) => exame.id,
    nome: (exame) => exame.examName,
    descricao: (exame) => exame.esocialDescription,
    perigo: (exame) => exame.esocialCode,
  },
  tipos: [],

  aplicacao: {
    id: (aplicacao) => aplicacao.id,
    itemId: (aplicacao) => aplicacao.examId,
    nome: nomeDaAplicacao,
  },

  valoresIniciais: (exame) => ({ isRequired: true, periodicityMonths: parseFrequencyToMonths(exame.defaultFrequency) }),
  chaveDoGrupo: (valores) => `${valores.isRequired ? OBRIGATORIO : OPCIONAL}|${valores.periodicityMonths ?? SEM_PERIODICIDADE}`,
  aplicar: aplicarExames,
  // Copia tudo o que a aplicação tem: é também como o "Desfazer" da remoção a recria.
  copiarDoHerdado: (destino, herdada) =>
    aplicarExames(
      destino,
      [Number(herdada.examId)],
      { isRequired: herdada.isRequired, periodicityMonths: herdada.periodicityMonths ?? null },
      { observations: herdada.observations ?? null }
    ),

  camposEditaveis: [
    {
      id: 'obrigatorio',
      rotulo: 'Obrigatório',
      ler: (aplicacao) => aplicacao.isRequired,
      render: (aplicacao, { gravar, somenteLeitura, recente }) =>
        somenteLeitura ? (
          <span>{aplicacao.isRequired ? 'Obrigatório' : 'Opcional'}</span>
        ) : (
          <ConferirAoMexer sugerido={recente === true} selo={<Confira />}>
            <SeletorDeObrigatorio
              valor={aplicacao.isRequired}
              rotulo={`Obrigatório em ${nomeDaAplicacao(aplicacao)}`}
              aoMudar={gravar}
            />
          </ConferirAoMexer>
        ),
    },
    {
      id: 'periodicidade',
      rotulo: 'Periodicidade',
      ler: (aplicacao) => aplicacao.periodicityMonths ?? null,
      render: (aplicacao, { gravar, somenteLeitura, recente }) =>
        somenteLeitura ? (
          <Typography variant="body2" color={aplicacao.periodicityMonths ? 'text.primary' : 'warning.dark'}>
            {textoDaPeriodicidade(aplicacao.periodicityMonths)}
          </Typography>
        ) : (
          <ConferirAoMexer sugerido={recente === true} selo={<Confira />}>
            <CampoDePeriodicidade
              valor={aplicacao.periodicityMonths ?? null}
              rotulo={`Periodicidade de ${nomeDaAplicacao(aplicacao)}`}
              aoMudar={gravar}
            />
          </ConferirAoMexer>
        ),
    },
  ],
  editar: async (destino, aplicacao, campo, valor) => {
    const exameId = Number(aplicacao.examId);
    if (campo === 'obrigatorio' && typeof valor === 'boolean') {
      await examApplicationService.updateForTarget(destino.nivel, destino.id, exameId, { isRequired: valor });
    } else if (campo === 'periodicidade') {
      await examApplicationService.updateForTarget(destino.nivel, destino.id, exameId, {
        periodicityMonths: periodicidadeDoValor(valor),
      });
    }
  },
  descreverEdicao: (aplicacao, campo, _antes, depois, alcance) =>
    descreverEdicao(aplicacao, campo.id, depois, alcance.funcionarios),
  acoesEmLote: ACOES_EM_LOTE,
  filtrosRapidos: [
    { id: 'sem-periodicidade', rotulo: 'Sem periodicidade', aceita: (aplicacao) => !aplicacao.periodicityMonths },
  ],
  JanelaDeDetalhe: DetalheDoExame,
  remover: async (destino, aplicacoes) => {
    await Promise.all(
      aplicacoes.map((aplicacao) =>
        examApplicationService.removeFromTarget(destino.nivel, destino.id, Number(aplicacao.examId))
      )
    );
  },
  textoDeRemocao,

  linkDaFicha: (destino) => (destino.nivel === 'occupation' ? `/occupations/${destino.id}` : null),
};
